<template>
  <div class="page-stack">
    <div class="sticky-toolbar print-controls">
      <a-card :bordered="false" class="sticky-toolbar-card">
        <a-space wrap align="center">
          <div style="font-weight:600">Дата:</div>
          <a-button @click="shiftDate(-1)"><LeftOutlined /></a-button>
          <a-date-picker
            v-model:value="dateValue"
            format="YYYY-MM-DD"
            :allowClear="false"
            inputReadOnly
            @change="load"
          />
          <a-button @click="shiftDate(1)"><RightOutlined /></a-button>
          <a-button @click="load" :loading="loading">Обновить</a-button>
          <a-button type="primary" @click="printPage">Печать</a-button>
        </a-space>
      </a-card>
    </div>

    <section class="print-sheet">
      <header class="print-header">
        <h1>ООО "Вега-инвест" комбинат питания "Туесок"</h1>
        <div>Дата заказа {{ dateStr() }}</div>
      </header>

      <div class="mobile-order-list">
        <a-card v-for="row in printableOrders" :key="`mobile-${row.id}`" size="small">
          <div class="mobile-contractor-name">{{ row.contractorName }}</div>

          <div class="mobile-meal-grid">
            <div v-for="meal in mealKeys" :key="`mobile-${row.id}-${meal}`" class="mobile-meal-card">
              <div class="mobile-meal-title">{{ mealLabel(meal) }}</div>
              <div class="meal-summary">
                {{ row.meals[meal] }} ({{ printableSummaryText(row.specials[meal]) }})
              </div>

              <div v-if="row.children[meal].length" class="children-table">
                <div>
                  <span
                    v-for="child in row.children[meal]"
                    :key="`mobile-${row.id}-${meal}-${child.child_id}`"
                    class="child-name"
                  >
                    {{ child.full_name }}
                    <span v-if="child.tag_icons?.length" class="print-tag-icons">
                      {{ child.tag_icons.join(' ') }}
                    </span>
                    <br>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </a-card>

        <a-card size="small" class="mobile-total-card">
          <div class="mobile-contractor-name">Итого</div>

          <div class="mobile-meal-grid">
            <div v-for="meal in mealKeys" :key="`mobile-total-${meal}`" class="mobile-meal-card">
              <div class="mobile-meal-title">{{ mealLabel(meal) }}</div>
              <div class="meal-summary">
                {{ totals.meals[meal] }} ({{ printableSummaryText(totals.specials[meal]) }})
              </div>
            </div>
          </div>
        </a-card>
      </div>

      <table class="print-table">
        <thead>
          <tr>
            <th>Заказчик</th>
            <th>Завтрак</th>
            <th>Обед</th>
            <th>Полдник</th>
            <th>Ужин</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="row in printableOrders" :key="row.id">
            <td class="contractor-cell">
              <div class="contractor-name">{{ row.contractorName }}</div>
            </td>

            <td v-for="meal in mealKeys" :key="`${row.id}-${meal}`" class="meal-cell">
              <table style="border: none;">
                <tbody>
                  <tr style="border: none;">
                    <td style="border: none;">
                      <div class="meal-summary">
                        {{ row.meals[meal] }} ({{ printableSummaryText(row.specials[meal]) }})
                      </div>
                      <span v-if="row.children[meal].length" class="children-table">
                        <div>
                          <span
                            v-for="child in row.children[meal]"
                            :key="`${row.id}-${meal}-${child.child_id}`"
                            class="child-name"
                          >
                            {{ child.full_name }}
                            <span v-if="child.tag_icons?.length" class="print-tag-icons">
                              {{ child.tag_icons.join(' ') }}
                            </span>
                            <br>
                          </span>
                        </div>
                      </span>
                      <span v-else class="children-empty"></span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>

          <tr class="total-row">
            <td class="contractor-cell">
              <div class="contractor-name">Итого</div>
            </td>

            <td v-for="meal in mealKeys" :key="`total-${meal}`" class="meal-cell">
              <div class="meal-summary">
                {{ totals.meals[meal] }} ({{ printableSummaryText(totals.specials[meal]) }})
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import dayjs from 'dayjs'
import { message } from 'ant-design-vue'
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue'
import { http } from '../../api/http'
import { getSpecialTypes } from '../../api/specialTypes'

const mealKeys = ['BREAKFAST', 'LUNCH', 'SNACK', 'DINNER']

const loading = ref(false)
const raw = ref(null)
const dateValue = ref(dayjs())
const specialTypes = ref([])

const printableTypeMap = computed(() => {
  const map = {}

  for (const item of specialTypes.value || []) {
    map[String(item.id)] = item
    map[normalizeSpecialName(item.name)] = item
  }

  return map
})

const printableOrders = computed(() =>
  (raw.value?.data?.orders || [])
    .map(item => ({
      id: item.order?.id,
      contractorName: item.contractor?.name || '-',
      meals: {
        BREAKFAST: item.meals?.BREAKFAST || 0,
        LUNCH: item.meals?.LUNCH || 0,
        SNACK: item.meals?.SNACK || 0,
        DINNER: item.meals?.DINNER || 0,
      },
      specials: {
        BREAKFAST: summarizeSpecials(item.specials?.BREAKFAST),
        LUNCH: summarizeSpecials(item.specials?.LUNCH),
        SNACK: summarizeSpecials(item.specials?.SNACK),
        DINNER: summarizeSpecials(item.specials?.DINNER),
      },
      children: {
        BREAKFAST: mapPrintChildren(item.children?.BREAKFAST),
        LUNCH: mapPrintChildren(item.children?.LUNCH),
        SNACK: mapPrintChildren(item.children?.SNACK),
        DINNER: mapPrintChildren(item.children?.DINNER),
      },
    }))
    .filter(row => mealKeys.some(meal => Number(row.meals[meal] || 0) > 0))
)

const totals = computed(() => {
  const result = {
    meals: {
      BREAKFAST: 0,
      LUNCH: 0,
      SNACK: 0,
      DINNER: 0,
    },
    specials: {
      BREAKFAST: emptySpecialSummary(),
      LUNCH: emptySpecialSummary(),
      SNACK: emptySpecialSummary(),
      DINNER: emptySpecialSummary(),
    },
  }

  for (const row of printableOrders.value) {
    for (const meal of mealKeys) {
      result.meals[meal] += Number(row.meals[meal] || 0)
      mergeSpecialSummary(result.specials[meal], row.specials[meal])
    }
  }

  return result
})

function normalizeSpecialName(value) {
  return String(value || '').trim().toLowerCase()
}

function emptySpecialSummary() {
  return {
    totalQty: 0,
    byType: {},
    printable: {},
  }
}

function mergeSpecialSummary(target, source) {
  target.totalQty += Number(source?.totalQty || 0)

  for (const [key, value] of Object.entries(source?.byType || {})) {
    target.byType[key] = Number(target.byType[key] || 0) + Number(value || 0)
  }

  for (const [key, value] of Object.entries(source?.printable || {})) {
    target.printable[key] = Number(target.printable[key] || 0) + Number(value || 0)
  }
}

function summarizeSpecials(items) {
  const summary = emptySpecialSummary()

  for (const item of items || []) {
    const qty = Number(item?.qty || 0)
    const meta =
      printableTypeMap.value[String(item?.special_type_id)] ||
      printableTypeMap.value[normalizeSpecialName(item?.name)]

    const typeKey = meta?.short_name || meta?.name || item?.name || `type-${item?.special_type_id || 'unknown'}`

    summary.totalQty += qty
    summary.byType[typeKey] = Number(summary.byType[typeKey] || 0) + qty

    if (meta?.for_print) {
      const printableKey = meta.short_name || meta.name
      summary.printable[printableKey] = Number(summary.printable[printableKey] || 0) + qty
    }
  }

  return summary
}

function printableSummaryText(summary) {
  const entries = Object.entries(summary?.printable || {}).filter(([, qty]) => Number(qty) > 0)

  if (!entries.length) return '0'

  return entries
    .map(([shortName, qty]) => `${shortName} - ${qty}`)
    .join(', ')
}

function mapPrintChildren(items) {
  return (items || []).map(child => ({
    ...child,
    tag_icons: (child.tags || [])
      .map(tagToPrintIcon)
      .filter(Boolean),
  }))
}

function tagToPrintIcon(tag) {
  const normalized = String(tag || '').trim().toLowerCase()

  if (['безглютеновая', 'без глютена', 'gluten free', 'gluten-free'].includes(normalized)) return '[БГ]'
  if (['безмолочная', 'без молока', 'dairy free', 'dairy-free'].includes(normalized)) return '[БМ]'
  if (['корректировка'].includes(normalized)) return '[К]'

  return ''
}

function dateStr() {
  return dateValue.value.format('YYYY-MM-DD')
}

function mealLabel(meal) {
  const map = {
    BREAKFAST: 'Завтрак',
    LUNCH: 'Обед',
    SNACK: 'Полдник',
    DINNER: 'Ужин',
  }

  return map[meal] || meal
}

function shiftDate(days) {
  dateValue.value = dayjs(dateValue.value).add(days, 'day')
  load()
}

async function load() {
  loading.value = true
  try {
    const [dayRes, specialRes] = await Promise.all([
      http.get(`/api/kitchen/day?date=${encodeURIComponent(dateStr())}`),
      getSpecialTypes(),
    ])

    raw.value = dayRes.data
    specialTypes.value = specialRes.data?.data || []
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Не удалось загрузить данные дня')
  } finally {
    loading.value = false
  }
}

function printPage() {
  window.print()
}

load()
</script>

<style scoped>
.page-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.sticky-toolbar {
  position: sticky;
  top: 16px;
  z-index: 20;
}

.sticky-toolbar-card :deep(.ant-card-body) {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}

.print-sheet {
  width: 100%;
  background: #fff;
  color: #111;
}

.print-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
}

.print-header h1 {
  margin: 0;
  font-size: 28px;
}

.print-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: #fff;
}

.print-table th,
.print-table td {
  border: 1px solid #222;
  padding: 8px;
  vertical-align: top;
}

.print-table th {
  text-align: center;
  font-weight: 700;
}

.contractor-cell {
  width: 14%;
}

.contractor-name {
  font-weight: 700;
}

.meal-cell {
  width: 21.5%;
}

.meal-summary {
  font-weight: 700;
  margin-bottom: 6px;
}

.children-table {
  font-size: 10px;
}

.child-name {
  white-space: nowrap;
}

.print-tag-icons {
  margin-left: 4px;
  font-size: 9px;
}

.mobile-order-list {
  display: none;
}

.mobile-contractor-name {
  font-weight: 700;
  margin-bottom: 12px;
}

.mobile-meal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.mobile-meal-card {
  border: 1px solid #d9d9d9;
  border-radius: 10px;
  padding: 10px;
}

.mobile-total-card {
  background: #f3f3f3;
}

.mobile-meal-title {
  font-weight: 600;
  margin-bottom: 6px;
}

.children-empty {
  color: #666;
  font-size: 12px;
}

.total-row td {
  background: #f3f3f3;
}

@media print {
  @page {
    margin: 2mm 6mm 6mm 6mm;
  }

  .print-controls {
    display: none !important;
  }

  :global(body) {
    margin: 0 !important;
  }

  .print-sheet {
    padding: 0;
  }

  .print-header h1 {
    font-size: 22px;
  }

  .print-table th,
  .print-table td {
    padding: 6px;
  }
}

@media (max-width: 767px) {
  .sticky-toolbar {
    top: 8px;
  }

  .mobile-order-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .print-table {
    display: none;
  }

  .print-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .mobile-meal-grid {
    grid-template-columns: 1fr;
  }
}
</style>
